import { useCallback, useEffect, useRef, useState } from 'react';

import type { SSEMessage } from '@/_shared/types';

import {
  insertBySeq,
  isTurnTerminalInHistory,
  mergeHistoryWithLiveMessages,
  sliceIntoTurns,
} from '../lib/turn-slicing';
import { applySSEEvent } from './sse-reducer';

export type TurnRecoveryStatus = 'recovering' | 'unavailable' | null;

interface UseSSEOptions {
  /** Keeps a final stream error attached to its chat when the active turn URL becomes null. */
  scopeKey?: string | null;
  /** Identity retained when a stopped stream's local busy state is cleared. */
  turnId?: string | null;
  /** Called after each SSE event is applied, with the resulting transcript. */
  onEvent?: (eventType: string, data: Record<string, unknown>, messages: SSEMessage[]) => void;
  /** Called when the SSE connection drops (error + disconnect). */
  onDisconnect?: () => void;
  recovery?: {
    sessionId: string | null;
    persistedMessages: SSEMessage[] | undefined;
    runtimeTurnId: string | null;
    refetchSession: (options?: { cancelRefetch?: boolean }) => Promise<unknown>;
    /** Controls are owner-only, but saved terminal events are merged for viewers too. */
    controlsEnabled?: boolean;
  };
}

interface UseSSEReturn {
  messages: SSEMessage[];
  connected: boolean;
  error: string | null;
  /** The current stream stopped without delivering a usable terminal event. */
  ended: boolean;
  endedTurnId: string | null;
  recovered: boolean;
  recoveryStatus: TurnRecoveryStatus;
  retryRecovery: () => void;
  recoverHistory: (history: SSEMessage[], turnId: string, abortMarker?: SSEMessage | null) => void;
  clearMessages: () => void;
  reconnect: () => void;
  addMessage: (msg: SSEMessage) => void;
  /** Add a message where its seq places it (`insertBySeq`), not at the end. */
  insertMessage: (msg: SSEMessage) => void;
  removeLastUserMessage: (content: string) => void;
  initializeMessages: (msgs: SSEMessage[]) => void;
  mergeMessages: (history: SSEMessage[]) => void;
  /**
   * Optimistically apply a turn_aborted event locally without waiting for SSE.
   * Idempotent: if no matching turn_start exists, messages are unchanged.
   */
  forceTurnAborted: (turnId: string, userMessage: string) => void;
}

const SSE_EVENT_TYPES = [
  'user_message',
  'assistant',
  'tool_use',
  'tool_result',
  'ui_message',
  'result',
  'turn_start',
  'turn_complete',
  'turn_aborted',
  'error',
  'skill_invoked',
  'memory_flushed',
  'compaction_started',
  // Interactive blocks (AI-411): non-terminal. awaiting_input pauses the turn on a question/approval
  // block (composer takeover); interactive_answer carries the resolution and renders the summary.
  'awaiting_input',
  'interactive_answer',
  // Ephemeral routine-editor side-channel (P12 §3.1): a live draft patch that mutates the editor
  // pane, NOT a durable chat bubble. Listened for here so it surfaces on the stream; the generic
  // chat reducer ignores it (see sse-reducer), and the workflow chat hook (T-FE3) routes it to the
  // draft store via `onEvent`.
  'routine_draft',
  'dashboard_draft',
  'audience_draft',
  'audience_draft_count',
  'chat_title',
  'stream_access_revoked',
  'stream_status',
] as const;

const EPHEMERAL_EVENT_TYPES = new Set<string>([
  'routine_draft',
  'dashboard_draft',
  'audience_draft',
  'audience_draft_count',
  'chat_title',
]);

const TERMINAL_EVENT_TYPES = new Set<string>(['turn_complete', 'error', 'turn_aborted']);

const MAX_RETRIES = 10;
const BASE_DELAY_MS = 1000;
const MAX_DELAY_MS = 30_000;
const RECOVERY_READS = 5;
const RECOVERY_INTERVAL_MS = 2_000;

interface StreamMemory {
  url: string | null;
  turnId: string | null;
  lastAppliedSeq: number;
  terminalApplied: boolean;
  accessRevoked: boolean;
  generation: number;
}

function streamUrlWithCursor(streamUrl: string, seq: number): string {
  if (seq <= 0) return streamUrl;
  const url = new URL(streamUrl, 'http://localhost');
  url.searchParams.set('after', String(seq));
  if (streamUrl.startsWith('http://') || streamUrl.startsWith('https://')) return url.toString();
  return `${url.pathname}${url.search}`;
}

function readSequence(data: Record<string, unknown>): number | null {
  const seq = data['seq'];
  if (typeof seq !== 'number' || !Number.isSafeInteger(seq) || seq < 0) return null;
  return seq;
}

function readEventIdSequence(id: string): number | null {
  if (!/^(0|[1-9][0-9]*)$/.test(id)) return null;
  const seq = Number(id);
  return Number.isSafeInteger(seq) ? seq : null;
}

export function useSSE(streamUrl: string | null, options?: UseSSEOptions): UseSSEReturn {
  const scopeKey = options?.scopeKey ?? streamUrl;
  const [messages, setMessages] = useState<SSEMessage[]>([]);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Keep the final state attached to its turn. A new URL can render before the effect below
  // clears old state, and must not inherit the previous turn's stopped state.
  const [endedStream, setEndedStream] = useState<{
    url: string;
    scopeKey: string | null;
    turnId: string | null;
  } | null>(null);
  const [recoveredStream, setRecoveredStream] = useState<{ scopeKey: string | null; turnId: string } | null>(null);
  const recoveredCallbacksRef = useRef<Set<string>>(new Set());
  const [recoveryRetryCount, setRecoveryRetryCount] = useState(0);
  const [exhaustedRecoveryKey, setExhaustedRecoveryKey] = useState<string | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  const retryCountRef = useRef(0);
  const protocolMismatchCountRef = useRef(0);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [connectTrigger, setConnectTrigger] = useState(0);

  // Synchronous transcript. Event handlers must not read the React state, which lags behind a burst.
  const messagesRef = useRef<SSEMessage[]>([]);
  const commitMessages = useCallback((next: SSEMessage[]) => {
    messagesRef.current = next;
    setMessages(next);
  }, []);
  const updateMessages = useCallback((updater: (prev: SSEMessage[]) => SSEMessage[]) => {
    const next = updater(messagesRef.current);
    messagesRef.current = next;
    setMessages(next);
  }, []);

  const baseMessagesRef = useRef<SSEMessage[]>([]);
  const prevUrlRef = useRef<string | null>(null);
  const streamStateRef = useRef<StreamMemory>({
    url: null,
    turnId: null,
    lastAppliedSeq: 0,
    terminalApplied: false,
    accessRevoked: false,
    generation: 0,
  });

  const onEventRef = useRef(options?.onEvent);
  const onDisconnectRef = useRef(options?.onDisconnect);
  const recoveryRef = useRef(options?.recovery);
  useEffect(() => {
    onEventRef.current = options?.onEvent;
  });
  useEffect(() => {
    onDisconnectRef.current = options?.onDisconnect;
  });
  useEffect(() => {
    recoveryRef.current = options?.recovery;
  });

  const streamTurnId = options?.turnId ?? null;

  useEffect(() => {
    if (!streamUrl) return;
    const activeUrl = streamUrl;

    if (streamStateRef.current.url !== streamUrl) {
      protocolMismatchCountRef.current = 0;
      streamStateRef.current = {
        url: streamUrl,
        turnId: streamTurnId,
        lastAppliedSeq: 0,
        terminalApplied: false,
        accessRevoked: false,
        generation: 0,
      };
    }

    // Strict Mode: if we're remounting with the same URL, restore the snapshot
    // taken during the previous cleanup instead of replaying from scratch.
    if (streamUrl === prevUrlRef.current && baseMessagesRef.current.length > 0) {
      commitMessages(baseMessagesRef.current);
    }
    prevUrlRef.current = streamUrl;

    retryCountRef.current = 0;
    setError(null);
    setEndedStream(null);
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }

    function scheduleRetry(): void {
      const state = streamStateRef.current;
      if (state.terminalApplied || state.accessRevoked) return;
      if (retryCountRef.current >= MAX_RETRIES) {
        setError(`Connection lost after ${MAX_RETRIES} attempts`);
        setEndedStream({ url: activeUrl, scopeKey, turnId: streamTurnId });
        return;
      }
      retryCountRef.current += 1;
      const attempt = retryCountRef.current;
      const delay = Math.min(BASE_DELAY_MS * 2 ** (attempt - 1), MAX_DELAY_MS);
      setError(`Reconnecting (${attempt}/${MAX_RETRIES})...`);
      retryTimeoutRef.current = setTimeout(openConnection, delay);
    }

    function openConnection(): void {
      const state = streamStateRef.current;
      if (state.terminalApplied || state.accessRevoked) return;
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }

      const generation = ++state.generation;
      let disconnected = false;

      function handleDisconnect(): void {
        if (disconnected) return;
        if (generation !== streamStateRef.current.generation) return;
        disconnected = true;
        eventSource.close();
        eventSourceRef.current = null;
        setConnected(false);

        if (streamStateRef.current.terminalApplied || streamStateRef.current.accessRevoked) return;

        onDisconnectRef.current?.();
        scheduleRetry();
      }

      const eventSource = new EventSource(streamUrlWithCursor(activeUrl, state.lastAppliedSeq), {
        withCredentials: true,
      });
      eventSourceRef.current = eventSource;

      eventSource.onopen = () => {
        if (generation !== streamStateRef.current.generation) return;
        setConnected(true);
        setError(null);
        retryCountRef.current = 0;
      };

      eventSource.onerror = () => {
        // A proxy 502/503 and an app error both appear as CLOSED without an HTTP status here.
        // Known final app outcomes arrive as stream_status; retry everything else with a cap.
        handleDisconnect();
      };

      const finishTerminal = (): void => {
        streamStateRef.current.terminalApplied = true;
        eventSource.close();
        eventSourceRef.current = null;
        setConnected(false);
      };

      for (const eventType of SSE_EVENT_TYPES) {
        eventSource.addEventListener(eventType, (event: MessageEvent) => {
          if (generation !== streamStateRef.current.generation) return;
          // EventSource also dispatches a plain Event named "error" for connection failures.
          // onerror handles those; only an SSE frame has message data to process here.
          if (!(event instanceof MessageEvent)) return;
          if (eventType === 'stream_access_revoked') {
            streamStateRef.current.accessRevoked = true;
            streamStateRef.current.generation += 1;
            setError('You no longer have access to this chat.');
            setEndedStream({ url: activeUrl, scopeKey, turnId: streamTurnId });
            setConnected(false);
            eventSource.close();
            eventSourceRef.current = null;
            return;
          }

          let data: Record<string, unknown>;
          try {
            const parsed: unknown = JSON.parse(event.data as string);
            if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
              throw new Error('SSE payload is not an object');
            }
            data = parsed as Record<string, unknown>;
          } catch {
            console.warn('Ignoring malformed chat stream event', eventType);
            const idSeq = readEventIdSequence(event.lastEventId);
            if (idSeq != null && !EPHEMERAL_EVENT_TYPES.has(eventType)) {
              streamStateRef.current.lastAppliedSeq = Math.max(streamStateRef.current.lastAppliedSeq, idSeq);
            }
            if (TERMINAL_EVENT_TYPES.has(eventType)) {
              setError('Chat stream ended with an invalid event.');
              setEndedStream({ url: activeUrl, scopeKey, turnId: streamTurnId });
              finishTerminal();
            }
            return;
          }

          if (eventType === 'stream_status') {
            const status = data['status'];
            setError(
              status === 'complete'
                ? 'This chat turn has ended. Refresh to see the latest messages.'
                : status === 'invalid_cursor'
                  ? 'This chat stream could not be resumed.'
                  : 'This chat turn is unavailable.',
            );
            setEndedStream({ url: activeUrl, scopeKey, turnId: streamTurnId });
            finishTerminal();
            return;
          }

          if (eventType === 'error' && data['message'] === 'No active chat session') {
            finishTerminal();
            return;
          }

          const seq = readSequence(data);
          if (event.lastEventId && seq != null && event.lastEventId !== String(seq)) {
            // A mismatched SSE id means the cursor cannot safely advance. Reconnect from the
            // last confirmed event once. A repeat is a protocol error, not a network outage.
            streamStateRef.current.generation += 1;
            eventSource.close();
            eventSourceRef.current = null;
            setConnected(false);
            protocolMismatchCountRef.current += 1;
            if (protocolMismatchCountRef.current === 1) scheduleRetry();
            else {
              setError('Chat stream is out of sync.');
              setEndedStream({ url: activeUrl, scopeKey, turnId: streamTurnId });
            }
            return;
          }
          if (seq != null && seq <= streamStateRef.current.lastAppliedSeq) return;
          if (seq != null && messagesRef.current.some((message) => message.seq === seq)) {
            streamStateRef.current.lastAppliedSeq = seq;
            if (TERMINAL_EVENT_TYPES.has(eventType)) finishTerminal();
            return;
          }

          try {
            commitMessages(applySSEEvent(messagesRef.current, eventType, data));
          } catch {
            console.warn('Ignoring unprocessable chat stream event', eventType);
            if (seq != null && !EPHEMERAL_EVENT_TYPES.has(eventType)) {
              streamStateRef.current.lastAppliedSeq = seq;
            }
            if (TERMINAL_EVENT_TYPES.has(eventType)) {
              setError('Chat stream ended with an invalid event.');
              setEndedStream({ url: activeUrl, scopeKey, turnId: streamTurnId });
              finishTerminal();
            }
            return;
          }

          if (seq != null && !EPHEMERAL_EVENT_TYPES.has(eventType)) {
            streamStateRef.current.lastAppliedSeq = seq;
            protocolMismatchCountRef.current = 0;
          }
          try {
            onEventRef.current?.(eventType, data, messagesRef.current);
          } catch {
            console.warn('Chat stream event callback failed', eventType);
          }
          if (TERMINAL_EVENT_TYPES.has(eventType)) {
            finishTerminal();
          }
        });
      }
    }

    openConnection();

    return () => {
      baseMessagesRef.current = messagesRef.current;
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
      setConnected(false);
    };
  }, [streamUrl, scopeKey, streamTurnId, connectTrigger, commitMessages]);

  const reconnect = useCallback(() => {
    if (streamStateRef.current.accessRevoked || streamStateRef.current.terminalApplied) return;
    retryCountRef.current = 0;
    protocolMismatchCountRef.current = 0;
    setError(null);
    setEndedStream(null);
    setConnectTrigger((prev) => prev + 1);
  }, []);

  const clearMessages = useCallback(() => {
    commitMessages([]);
  }, [commitMessages]);

  const addMessage = useCallback(
    (msg: SSEMessage) => {
      updateMessages((prev) => {
        if (
          msg.type === 'interactive_answer' &&
          prev.some((m) => m.type === 'interactive_answer' && m.blockId === msg.blockId)
        ) {
          return prev;
        }
        return [...prev, msg];
      });
    },
    [updateMessages],
  );

  const insertMessage = useCallback(
    (msg: SSEMessage) => {
      updateMessages((prev) => insertBySeq(prev, msg));
    },
    [updateMessages],
  );

  /** Remove the last seq-less (optimistic) user_message matching `content`.
   *  Only retracts messages that haven't been confirmed by the server (no seq).
   *  This prevents deleting canonical SSE-delivered messages when a network
   *  blip causes a client-side error after the server already accepted the send. */
  const removeLastUserMessage = useCallback(
    (content: string) => {
      updateMessages((prev) => {
        for (let i = prev.length - 1; i >= 0; i--) {
          const m = prev[i]!;
          if (m.type === 'user_message' && m.seq == null && m.content === content) {
            return [...prev.slice(0, i), ...prev.slice(i + 1)];
          }
        }
        return prev;
      });
    },
    [updateMessages],
  );

  const initializeMessages = useCallback(
    (msgs: SSEMessage[]) => {
      commitMessages(msgs);
    },
    [commitMessages],
  );

  const mergeMessages = useCallback(
    (history: SSEMessage[]) => {
      updateMessages((prev) => mergeHistoryWithLiveMessages(history, prev));
    },
    [updateMessages],
  );

  /** Replay saved turn events through the same reducer and callback used by EventSource. */
  const recoverHistory = useCallback(
    (history: SSEMessage[], turnId: string, abortMarker?: SSEMessage | null) => {
      const currentStream = streamStateRef.current;
      const anotherTurnIsStreaming =
        streamUrl !== null && currentStream.url === streamUrl && currentStream.turnId !== turnId;
      const startIndex = history.findIndex((message) => message.type === 'turn_start' && message.turnId === turnId);
      const previous = messagesRef.current;
      const appliedSeqs = new Set(previous.flatMap((message) => (message.seq == null ? [] : [message.seq])));
      let replayed = previous;
      for (let index = 0; index < history.length; index++) {
        const message = history[index]!;
        const alreadyPresent = message.seq != null && appliedSeqs.has(message.seq);
        if (alreadyPresent) continue;
        replayed = applySSEEvent(replayed, message.type, message);
        if (message.seq != null) appliedSeqs.add(message.seq);
        if (startIndex >= 0 && index < startIndex) continue;
        if (startIndex < 0 && !('turnId' in message && message.turnId === turnId)) continue;
        if (anotherTurnIsStreaming) continue;
        const callbackKey = `${scopeKey ?? ''}:${turnId}:${message.seq ?? index}`;
        if (recoveredCallbacksRef.current.has(callbackKey)) continue;
        recoveredCallbacksRef.current.add(callbackKey);
        try {
          onEventRef.current?.(message.type, message, replayed);
        } catch {
          console.warn('Recovered chat event callback failed', message.type);
        }
      }

      // The saved order is canonical. Reducer replay first upgrades a matching optimistic prompt
      // with its saved seq, so this merge also removes that local duplicate.
      replayed = mergeHistoryWithLiveMessages(history, replayed);
      const matchingAbort = abortMarker?.type === 'turn_aborted' && abortMarker.turnId === turnId ? abortMarker : null;
      if (matchingAbort) {
        replayed = applySSEEvent(replayed, 'turn_aborted', matchingAbort);
        const callbackKey = `${scopeKey ?? ''}:${turnId}:abort`;
        if (!anotherTurnIsStreaming && !recoveredCallbacksRef.current.has(callbackKey)) {
          recoveredCallbacksRef.current.add(callbackKey);
          try {
            onEventRef.current?.('turn_aborted', matchingAbort, replayed);
          } catch {
            console.warn('Recovered chat event callback failed', 'turn_aborted');
          }
        }
      }
      commitMessages(replayed);
      if (matchingAbort || isTurnTerminalInHistory(history, turnId)) {
        setRecoveredStream({ scopeKey, turnId });
        const state = streamStateRef.current;
        if (state.url && streamUrl === state.url && state.turnId === turnId) {
          state.terminalApplied = true;
          state.generation += 1;
          eventSourceRef.current?.close();
          eventSourceRef.current = null;
          if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
            retryTimeoutRef.current = null;
          }
          setConnected(false);
          setError(null);
          setEndedStream({ url: state.url, scopeKey, turnId });
        }
      }
    },
    [commitMessages, scopeKey, streamUrl],
  );

  const persistedMessages = options?.recovery?.persistedMessages;
  const recoverySessionId = options?.recovery?.sessionId ?? null;
  const runtimeTurnId = options?.recovery?.runtimeTurnId ?? null;
  const controlsEnabled = options?.recovery?.controlsEnabled ?? true;
  const persistedTurn = sliceIntoTurns(persistedMessages ?? []).at(-1);
  const liveTurn = sliceIntoTurns(messages).at(-1);

  // A viewer can receive a final saved event from a read while their stream is still retrying.
  // Merge that terminal even though the recovery controls below remain owner-only.
  useEffect(() => {
    if (
      recoverySessionId &&
      persistedMessages &&
      liveTurn?.status === 'active' &&
      liveTurn.turnId &&
      isTurnTerminalInHistory(persistedMessages, liveTurn.turnId)
    ) {
      recoverHistory(persistedMessages, liveTurn.turnId);
    }
  }, [recoverySessionId, persistedMessages, liveTurn?.status, liveTurn?.turnId, recoverHistory]);

  const unfinishedTurnId =
    controlsEnabled &&
    recoverySessionId &&
    !runtimeTurnId &&
    !streamTurnId &&
    persistedTurn?.status === 'active' &&
    persistedTurn.turnId &&
    !isTurnTerminalInHistory(messages, persistedTurn.turnId)
      ? persistedTurn.turnId
      : null;
  const recoveryKey = unfinishedTurnId ? `${recoverySessionId}:${unfinishedTurnId}:${recoveryRetryCount}` : null;
  useEffect(() => {
    if (!recoveryKey || recoveryKey === exhaustedRecoveryKey) return;
    let reads = 0;
    let timer: ReturnType<typeof setTimeout>;
    const read = (): void => {
      void recoveryRef.current?.refetchSession({ cancelRefetch: false }).catch(() => undefined);
      reads += 1;
      timer = setTimeout(() => {
        if (reads < RECOVERY_READS) read();
        else setExhaustedRecoveryKey(recoveryKey);
      }, RECOVERY_INTERVAL_MS);
    };
    read();
    return () => clearTimeout(timer);
  }, [recoveryKey, exhaustedRecoveryKey]);
  const retryRecovery = useCallback(() => setRecoveryRetryCount((count) => count + 1), []);
  const recoveryStatus: TurnRecoveryStatus =
    recoveryKey === null ? null : recoveryKey === exhaustedRecoveryKey ? 'unavailable' : 'recovering';

  const forceTurnAborted = useCallback(
    (turnId: string, userMessage: string) => {
      updateMessages((prev) => applySSEEvent(prev, 'turn_aborted', { turnId, userMessage }));
    },
    [updateMessages],
  );

  return {
    messages,
    connected,
    error,
    ended:
      endedStream !== null &&
      endedStream.scopeKey === scopeKey &&
      (streamUrl === null || endedStream.url === streamUrl),
    endedTurnId: endedStream?.scopeKey === scopeKey ? endedStream.turnId : null,
    recovered:
      recoveredStream?.scopeKey === scopeKey &&
      (endedStream ? recoveredStream.turnId === endedStream.turnId : streamUrl === null),
    recoveryStatus,
    retryRecovery,
    recoverHistory,
    clearMessages,
    reconnect,
    addMessage,
    insertMessage,
    removeLastUserMessage,
    initializeMessages,
    mergeMessages,
    forceTurnAborted,
  };
}
